import { PlanCardConfig } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import { memo } from 'react';
import { CreditSelect } from './components/credit-select';
import { PlanBillingCycle } from './components/plan-billing-cycle';
import { PlanComparePlans } from './components/plan-compare-plans';
import { PlanCreditRate } from './components/plan-credit-rate';
import { PlanDynamicAction } from './components/plan-dynamic-action';
import { PlanInclusion } from './components/plan-inclusion';
import { PlanPrice } from './components/plan-price';
import { PlanTags } from './components/plan-tags';
import { PlanTitle } from './components/plan-title';
import { UpcomingPlanBadge } from './components/upcoming-plan-badge';
import { usePlanCardContext } from './context';
import { PlanCardProvider } from './context/provider';
import * as Styled from './styles';
import * as SX from './sx';
import type { PlanCardProps as Props } from './types';

const PlanCardBody = memo(({ className }: Pick<Props, 'className'>) => {
  const { isCompact, isMostPopular, isUpcoming } = usePlanCardContext();

  return (
    <Box sx={SX.wrapper}>
      <Styled.Wrapper
        className={classnames('PlanCard', className)}
        sx={
          isCompact
            ? SX.compactRoot(isMostPopular)
            : SX.root({ isMostPopular, isUpcoming })
        }
      >
        <UpcomingPlanBadge />
        <PlanTags />
        <PlanTitle />
        <PlanPrice />
        <PlanCreditRate />
        <PlanBillingCycle />
        <CreditSelect />
        <Box sx={isCompact ? SX.actionSlot : undefined}>
          <PlanDynamicAction />
        </Box>
        {!isCompact && <PlanInclusion />}
        {!isCompact && <PlanComparePlans />}
      </Styled.Wrapper>
    </Box>
  );
});

PlanCardBody.displayName = 'PlanCardBody';

export const PlanCard = memo(
  ({
    className,
    plans,
    yearly,
    isFree,
    config,
    isLoading,
    variant = 'default',
    isMostPopular,
  }: Props) => {
    const isPaid = !isFree;

    return (
      <PlanCardProvider
        isPaid={isPaid}
        plans={plans}
        yearly={yearly}
        config={config || ({} as PlanCardConfig)}
        isLoading={isLoading}
        variant={variant}
        isMostPopular={isMostPopular}
      >
        <PlanCardBody className={className} />
      </PlanCardProvider>
    );
  }
);

PlanCard.displayName = 'PlanCard';
